'use client';

import { clsx } from 'clsx';
import { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import { NEW_BLOCK_ID } from '@unifyapps/defs/constants';
import useDebugging from '../../hooks/useDebugging';
import {
  useGetDeviceDetailsInStateStore,
  useGlobalStateStore,
} from '../../stores/GlobalStateStore';
import { InterfaceModes, useInterfaceStore } from '../../stores/InterfaceStore';
import { useRegistryContext } from '../RegistryProvider';
import PageLayout, { PageFooter, PageHeader, PageWorkspace } from '../../../components/PageLayout';
import { getPageLayout } from './getPageBody';
import type { InterfacePageProps } from './types';

type Content = { blockIds?: string[] } | undefined;

const getIsBlockEmpty = (content: Content, mode: InterfaceModes) => {
  if (mode === InterfaceModes.BUILDER) return false;
  if (!content) return true;
  if (!content.blockIds?.length) return true;
  return content.blockIds.length === 1 && content.blockIds[0] === NEW_BLOCK_ID;
};

export function InterfacePage({
  name,
  layout,
  className,
  workspaceClassName,
  device,
  BlockRenderer,
  isHeaderEmpty,
  isFooterEmpty,
  colorScheme,
}: InterfacePageProps) {
  const { header, body, footer } = getPageLayout(layout);

  useDebugging();

  return (
    <>
      {!process.env.DELTA_MATRIX && name ? <title>{name}</title> : null}
      <PageLayout className={className} dataColorScheme={colorScheme}>
        {isHeaderEmpty ? null : (
          <PageHeader headerClassName="!p-0 !basis-auto">
            {header ? <BlockRenderer blockId={header} key={header} parentBlockId={null} /> : null}
          </PageHeader>
        )}
        {body ? (
          <PageWorkspace
            className={clsx(
              'overflow-y-auto',
              {
                'rounded-t-none': !header,
                'rounded-b-3xl': Boolean(footer),
                '!me-0': (!header && !footer) || device === DeviceVariantType.MOBILE,
              },
              workspaceClassName,
            )}
          >
            {body ? <BlockRenderer blockId={body} key={body} parentBlockId={null} /> : null}
          </PageWorkspace>
        ) : null}
        {isFooterEmpty ? null : (
          // we don't want to add padding to the footer in no-code app
          <PageFooter className="!p-0">
            {footer ? <BlockRenderer blockId={footer} key={footer} parentBlockId={null} /> : null}
          </PageFooter>
        )}
      </PageLayout>
    </>
  );
}

export type Props = {
  className?: string;
  workspaceClassName?: string;
};

function InterfacePageContainer(props: Props) {
  const layout = useInterfaceStore().use.page.layout();
  const name = useInterfaceStore().use.page.name();
  const { device } = useGetDeviceDetailsInStateStore();
  const blocks = useGlobalStateStore().use.blocks();
  const BlockRenderer = useRegistryContext().registry.getBlockRenderer();
  const { header, footer } = getPageLayout(layout);
  const mode = useInterfaceStore().use.mode();

  const isFooterEmpty = footer ? getIsBlockEmpty(blocks[footer]?.content as Content, mode) : true;
  const isHeaderEmpty = header ? getIsBlockEmpty(blocks[header]?.content as Content, mode) : true;

  return (
    <InterfacePage
      BlockRenderer={BlockRenderer}
      device={device}
      isFooterEmpty={isFooterEmpty}
      isHeaderEmpty={isHeaderEmpty}
      layout={layout}
      name={name}
      {...props}
    />
  );
}

export default InterfacePageContainer;
